import { ErrorResponse } from '@/typings/api/ErrorResponse'
import { ErrorBag, FieldError } from 'vee-validate'
import { InitPluginsOptions } from '@/typings/common'

import '@/assets/scss/noty/index.scss'
import Noty from 'noty'
import { getErrorMessages } from '@/helpers/getErrorMessages'


export interface EROptions {
  $errors: ErrorBag;
  $fields: FieldError;
  message: string;
}

const DEFAULT_OPTIONS: Noty.Options = {
  layout: 'topRight',
  theme: 'mint',
  timeout: 6000,
}

const ERRORS_OPTIONS: Noty.Options = {
  ...DEFAULT_OPTIONS,
  type: 'error',
}

const WARNING_OPTIONS: Noty.Options = {
  ...DEFAULT_OPTIONS,
  type: 'warning',
}

const INFO_OPTIONS: Noty.Options = {
  ...DEFAULT_OPTIONS,
  type: 'info',
}

const EXCLUDE_MESSAGES: string[] = [
  'Не авторизован (401)',
]

export class Notice {
  public create(data: Noty.Options | string): this {
    import(
      /* webpackChunkName: "noty" */
      'noty'
    ).then((module) => {
      const NotyClass = module.default

      if (typeof data === 'string') {
        if (EXCLUDE_MESSAGES.includes(data)) return null
        new NotyClass({ ...DEFAULT_OPTIONS, text: data }).show()
      } else {
        if (data.text && EXCLUDE_MESSAGES.includes(data.text)) return null
        new NotyClass({ ...DEFAULT_OPTIONS, ...data }).show()
      }

      return null
    }).catch(() => null)

    return this
  }

  public errors(errorResponse: ErrorResponse, options = {}): this {
    if (errorResponse && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.dir(errorResponse)
    }

    getErrorMessages(errorResponse, options).forEach((text) => {
      if (EXCLUDE_MESSAGES.includes(text)) return
      const opt = {
        ...ERRORS_OPTIONS,
        ...options,
        text,
        id: text,
      }
      this.create(opt)
    })

    return this
  }

  public error(text: string, data: Noty.Options = {}): this {
    return this.create({
      ...ERRORS_OPTIONS,
      ...data,
      text,
    })
  }

  public warning(text: string, data: Noty.Options = {}): this {
    return this.create({
      ...WARNING_OPTIONS,
      ...data,
      text,
    })
  }

  public info(text: string, data: Noty.Options = {}): this {
    return this.create({
      ...INFO_OPTIONS,
      ...data,
      text,
    })
  }

  public errorsResponseForm(errorResponse: ErrorResponse, options: EROptions): this {
    const { $errors, $fields, message } = options

    if (!Array.isArray(errorResponse?.errors) || !$errors || !$fields) {
      this.errors(errorResponse, options)
      return this
    }

    const errors = errorResponse.errors.filter((error) => {
      if (!error.field) return true
      if (!(error.field in $fields)) return true
      const data = { field: error.field, msg: error.message }
      $errors.add(data)
      return false
    })

    this.errors({ errors, status: errorResponse.status }, { message })

    return this
  }

  public validatorFormError() {
    const text = 'Ошибка валидации в форме'
    this.create({ ...ERRORS_OPTIONS, text })
  }
}

export const initPlugin = ({ Vue: { prototype }, app }: InitPluginsOptions) => {
  const notice = new Notice()
  // eslint-disable-next-line no-param-reassign
  app.notice = notice

  Object.defineProperty(prototype, '$notice', {
    get(this: Vue) {
      const options = this.$options as unknown as { notice?: Notice }
      return options.notice || this.$parent.$notice
    },
  })
}
